import { Text } from '../../../../../cdl/Text/Text';
import { Tooltip } from '../../../../../cdl/Tooltip/Tooltip';
import { formatDateTime } from '../../../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Characters } from '../../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../../common/hooks/useEnums';
import { useProductStringFormat } from '../../../../../common/hooks/useProductStringFormat';
import { MiddleDotSeparator } from '../../../../../common/Separators/MiddleDotSeparator';
import { Box } from '../../../../../common/ui/Box';
import { Port } from '../../../../../port/Port';
import { NoticeDaysDisplay } from '../../../components/OfferCard/components/NoticeDaysDisplay';
import { useOfferCardContext } from '../../../components/OfferCard/hooks/useOfferCardContext';
import { useOfferPaymentTerms } from '../../../components/OfferCard/hooks/useOfferPaymentTerms';

export const OfferDetails = () => {
    const { offer, order } = useOfferCardContext();
    const { getHumanReadableValue } = useEnums();
    const formatProductString = useProductStringFormat();
    const paymentTermString = useOfferPaymentTerms(offer);
    const activeItems = offer.items.filter((item) => !item.deleted);
    const products = activeItems
        .filter((item) => !item.isTranslationMissing() && item.productId)
        .map((item) => formatProductString(item.product!));
    const maxVisibleProductNames = offer.isSpot() ? 3 : 2;
    const displayedProducts =
        products.length > maxVisibleProductNames ? products.slice(0, maxVisibleProductNames) : products;

    if (offer.isUnquoted() && !offer.wasQuoted()) {
        return (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16} paddingTop={8} style={{ userSelect: 'none' }}>
                <Box display="flex" flexDirection="column" rowGap={4} alignItems="start">
                    {displayedProducts.length
                        ? displayedProducts.map((product, index) => (
                              <Text key={`${product}-${index}`} variant="extraSmall">
                                  {product}
                              </Text>
                          ))
                        : null}

                    {products.length > displayedProducts.length ? (
                        <Text variant="extraSmall" color="accent.foreground">
                            {translate('order.detail.showMore')}
                        </Text>
                    ) : null}
                </Box>

                <Box display="flex" flexDirection="column" rowGap={4} alignItems="start" />
            </Box>
        );
    }

    return (
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={16} paddingTop={8} style={{ userSelect: 'none' }}>
            <Box display="flex" flexDirection="column" rowGap={4} alignItems="start">
                {displayedProducts.length ? (
                    displayedProducts.map((product, index) => (
                        <Text key={`${product}-${index}`} variant="extraSmall">
                            {product}
                        </Text>
                    ))
                ) : (
                    <Text variant="extraSmall">{Characters.EN_DASH}</Text>
                )}

                {products.length > displayedProducts.length ? (
                    <Text variant="extraSmall" color="accent.foreground">
                        {translate('order.detail.showMore')}
                    </Text>
                ) : null}
            </Box>

            <Box display="flex" flexDirection="column" rowGap={4} alignItems="start">
                <Box display="flex">
                    <Tooltip label={translate('order.detail.coreData.dateDelivery')} showArrow side="left">
                        <Text
                            variant="extraSmall"
                            color={offer.changes.dateDeliveryChanged ? 'attention.foreground' : 'inherit'}
                            fontWeight={offer.changes.dateDeliveryChanged ? 600 : 400}
                        >
                            {formatDateTime({
                                date: offer.dateDelivery,
                                format: 'PP',
                            })}
                        </Text>
                    </Tooltip>

                    {offer.noticeDays ? (
                        <Text variant="extraSmall">
                            <MiddleDotSeparator />
                        </Text>
                    ) : null}

                    <NoticeDaysDisplay
                        noticeDays={offer.noticeDays}
                        dateDelivery={offer.dateDelivery}
                        orderState={order.state}
                    />
                </Box>

                {offer.port && offer.changes.portChanged ? (
                    <Tooltip label={translate('order.detail.offerCard.portChanged')} showArrow side="left">
                        <Text variant="extraSmall" color="attention.foreground" fontWeight={600}>
                            <Port port={offer.port} />
                        </Text>
                    </Tooltip>
                ) : null}

                <Tooltip label={translate('order.supplyMode.label')} showArrow side="left">
                    <Text variant="extraSmall">{getHumanReadableValue('supplyMode', offer.supplyMode)}</Text>
                </Tooltip>

                <Tooltip label={translate('order.detail.paymentTerm')} showArrow side="left">
                    <Text variant="extraSmall">{paymentTermString}</Text>
                </Tooltip>

                {offer.vendorReference ? (
                    <Tooltip label={translate('order.vendorreference')} side="left" showArrow>
                        <Text variant="extraSmall">{offer.vendorReference}</Text>
                    </Tooltip>
                ) : null}
            </Box>
        </Box>
    );
};
